import { api } from "../lib/api";

export type RateUnit = "hour" | "day" | "fixed" | "item";

export interface Project {
  id: string;
  client_id: string;
  name: string;
  code: string | null;
  rate: string;
  rate_unit: RateUnit;
  currency: string | null;
  active: boolean;
  notes: string | null;
  // Timesheet rollup conversion factors (05 § Timesheets) — only meaningful for
  // hour/day-rate projects, but always present (server defaults 8.00/5.00/20.00).
  hours_per_day: string;
  days_per_week: string;
  days_per_month: string;
  created_at: string;
  updated_at: string;
}

export type ProjectInput = Omit<Project, "id" | "client_id" | "created_at" | "updated_at">;

/** GET /projects rows (company-wide, client-grouped picker) — client_name server-joined. */
export interface ProjectWithClient extends Project {
  client_name: string;
}

export function listProjects(clientId: string): Promise<Project[]> {
  return api.get<Project[]>(`/api/clients/${clientId}/projects`);
}

export interface ListCompanyProjectsFilters {
  active?: boolean;
  page?: number;
  page_size?: number;
}

export function listCompanyProjects(
  filters: ListCompanyProjectsFilters = {},
): Promise<{ items: ProjectWithClient[]; total: number }> {
  const params = new URLSearchParams();
  if (filters.active) params.set("active", "true");
  if (filters.page) params.set("page", String(filters.page));
  if (filters.page_size) params.set("page_size", String(filters.page_size));
  const query = params.toString();
  return api.getPaged<ProjectWithClient>(`/api/projects${query ? `?${query}` : ""}`);
}

export function getProject(id: string): Promise<Project> {
  return api.get<Project>(`/api/projects/${id}`);
}

export function createProject(clientId: string, input: ProjectInput): Promise<Project> {
  return api.post<Project>(`/api/clients/${clientId}/projects`, input);
}

export function updateProject(
  id: string,
  input: ProjectInput & { updated_at: string },
): Promise<Project> {
  return api.put<Project>(`/api/projects/${id}`, input);
}

export function deactivateProject(id: string): Promise<Project> {
  return api.delete(`/api/projects/${id}`);
}
